import React, { useState } from 'react';
import { Empleado, BalanceEmpleado, PeriodoVacaciones, Departamento, TipoLicencia } from '../tipos/vacaciones';
import FormularioEmpleado from './FormularioEmpleado';
import FormularioPeriodo from './FormularioPeriodo';
import FormularioBalance from './FormularioBalance';
import ModalConfirmacion from './ModalConfirmacion';
import * as empleadoService from '../services/empleadoService';
import './ListaEmpleados.css';

const ANIO_BALANCE = new Date().getFullYear();

interface PropiedadesListaEmpleados {
  empleados: Empleado[];
  balances: BalanceEmpleado[];
  periodos: PeriodoVacaciones[];
  departamentos: Departamento[];
  tiposLicencia: TipoLicencia[];
  onDatosActualizados: () => void;
  esAdmin?: boolean;
}

const ListaEmpleados: React.FC<PropiedadesListaEmpleados> = ({
  empleados,
  balances,
  periodos,
  departamentos,
  tiposLicencia,
  onDatosActualizados,
  esAdmin = false,
}) => {
  const [filtroDepartamento, setFiltroDepartamento] = useState<string>('all');
  const [filtroEstado, setFiltroEstado] = useState<'all' | 'available' | 'on-leave'>('all');
  const [ordenarPor, setOrdenarPor] = useState<'name' | 'department' | 'used' | 'remaining'>('name');
  const [ordenDireccion, setOrdenDireccion] = useState<'asc' | 'desc'>('asc');

  // Estados de modales
  const [mostrarFormEmpleado, setMostrarFormEmpleado] = useState(false);
  const [mostrarFormPeriodo, setMostrarFormPeriodo] = useState(false);
  const [mostrarFormBalance, setMostrarFormBalance] = useState(false);
  const [mostrarConfirmEliminar, setMostrarConfirmEliminar] = useState(false);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState<Empleado | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const obtenerDepartamentos = () => {
    const conjuntoDepartamentos = new Set(empleados.map(e => e.departamento));
    const depts = Array.from(conjuntoDepartamentos).sort();
    return depts;
  };

  const empleadoEstaEnLicencia = (idEmpleado: number) => {
    const hoy = new Date();
    return periodos.some(periodo => {
      const inicio = new Date(periodo.fechaInicio);
      const fin = new Date(periodo.fechaFin);
      return periodo.idEmpleado === idEmpleado && inicio <= hoy && fin >= hoy;
    });
  };

  const obtenerLicenciaActual = (idEmpleado: number): PeriodoVacaciones | null => {
    const hoy = new Date();
    return periodos.find(periodo => {
      const inicio = new Date(periodo.fechaInicio);
      const fin = new Date(periodo.fechaFin);
      return periodo.idEmpleado === idEmpleado && inicio <= hoy && fin >= hoy;
    }) || null;
  };

  const obtenerProximaLicencia = (idEmpleado: number): PeriodoVacaciones | null => {
    const hoy = new Date();
    const futuras = periodos
      .filter(periodo => {
        const inicio = new Date(periodo.fechaInicio);
        return periodo.idEmpleado === idEmpleado && inicio > hoy;
      })
      .sort((a, b) => new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime());

    return futuras.length > 0 ? futuras[0] : null;
  };

  const empleadosFiltrados = empleados
    .filter(empleado => {
      const coincideDepartamento = filtroDepartamento === 'all' || empleado.departamento === filtroDepartamento;
      const estaEnLicencia = empleadoEstaEnLicencia(empleado.id);
      const coincijeEstado =
        filtroEstado === 'all' ||
        (filtroEstado === 'on-leave' && estaEnLicencia) ||
        (filtroEstado === 'available' && !estaEnLicencia);

      return empleado.estaActivo && coincideDepartamento && coincijeEstado;
    })
    .sort((a, b) => {
      let comparacion = 0;
      const balanceA = balances.find(bal => bal.idEmpleado === a.id);
      const balanceB = balances.find(bal => bal.idEmpleado === b.id);

      switch (ordenarPor) {
        case 'name':
          comparacion = a.nombre.localeCompare(b.nombre);
          break;
        case 'department':
          comparacion = a.departamento.localeCompare(b.departamento);
          break;
        case 'used':
          comparacion = (balanceA?.diasUsados || 0) - (balanceB?.diasUsados || 0);
          break;
        case 'remaining':
          comparacion = (balanceA?.diasRestantes || 0) - (balanceB?.diasRestantes || 0);
          break;
      }

      return ordenDireccion === 'asc' ? comparacion : -comparacion;
    });

  const manejarOrden = (nuevoOrdenarPor: 'name' | 'department' | 'used' | 'remaining') => {
    if (ordenarPor === nuevoOrdenarPor) {
      setOrdenDireccion(ordenDireccion === 'asc' ? 'desc' : 'asc');
    } else {
      setOrdenarPor(nuevoOrdenarPor);
      setOrdenDireccion('asc');
    }
  };

  const obtenerIconoTipo = (tipo: string) => {
    switch (tipo) {
      case 'Vacaciones':
        return 'fas fa-umbrella-beach';
      case 'Licencia médica':
        return 'fas fa-heartbeat';
      case 'Día de Beneficio':
        return 'fas fa-star';
      case 'Compensatorio':
        return 'fas fa-clock';
      default:
        return 'fas fa-calendar';
    }
  };

  const obtenerColorTipo = (tipo: string) => {
    switch (tipo) {
      case 'Vacaciones':
        return 'vacaciones';
      case 'Licencia médica':
        return 'medica';
      case 'Día de Beneficio':
        return 'especial';
      case 'Compensatorio':
        return 'compensatorio';
      default:
        return 'vacaciones';
    }
  };

  const formatearFecha = (cadenaFecha: string) => {
    return new Date(cadenaFecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const formatearRangoFechas = (fechaInicio: string, fechaFin: string) => {
    if (fechaInicio === fechaFin) {
      return formatearFecha(fechaInicio);
    }
    return `${formatearFecha(fechaInicio)} - ${formatearFecha(fechaFin)}`;
  };

  const totalEnLicencia = empleadosFiltrados.filter(emp => empleadoEstaEnLicencia(emp.id)).length;

  // Handlers de modales
  const abrirNuevoEmpleado = () => {
    setEmpleadoSeleccionado(null);
    setMostrarFormEmpleado(true);
  };

  const abrirEditarEmpleado = (empleado: Empleado) => {
    setEmpleadoSeleccionado(empleado);
    setMostrarFormEmpleado(true);
  };

  const abrirAsignarVacaciones = (empleado: Empleado) => {
    setEmpleadoSeleccionado(empleado);
    setMostrarFormPeriodo(true);
  };

  const abrirConfigurarBalance = (empleado: Empleado) => {
    setEmpleadoSeleccionado(empleado);
    setMostrarFormBalance(true);
  };

  const abrirConfirmarEliminar = (empleado: Empleado) => {
    setEmpleadoSeleccionado(empleado);
    setMostrarConfirmEliminar(true);
  };

  const manejarGuardarEmpleado = () => {
    setMostrarFormEmpleado(false);
    setEmpleadoSeleccionado(null);
    onDatosActualizados();
  };

  const manejarGuardarPeriodo = () => {
    setMostrarFormPeriodo(false);
    setEmpleadoSeleccionado(null);
    onDatosActualizados();
  };

  const manejarGuardarBalance = () => {
    setMostrarFormBalance(false);
    setEmpleadoSeleccionado(null);
    onDatosActualizados();
  };

  const manejarEliminarEmpleado = async () => {
    if (!empleadoSeleccionado) return;
    setEliminando(true);
    try {
      await empleadoService.remove(empleadoSeleccionado.id);
      setMostrarConfirmEliminar(false);
      setEmpleadoSeleccionado(null);
      onDatosActualizados();
    } catch (err) {
      // El error se mostrara en la consola; en un caso real
      // se podria mostrar un toast
      console.error('Error al eliminar empleado:', err);
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="employee-list">
      <div className="list-header">
        <div className="list-header-content">
          <div>
            <h2>Estado de Empleados</h2>
            <p>Consulta el estado actual y proximas licencias de todos los empleados</p>
          </div>
          {esAdmin && (
            <button className="btn-nuevo" onClick={abrirNuevoEmpleado}>
              <i className="fas fa-plus"></i>
              Nuevo Empleado
            </button>
          )}
        </div>
      </div>

      <div className="list-filters">
        <div className="filter-group">
          <label htmlFor="department-filter">Departamento:</label>
          <select
            id="department-filter"
            value={filtroDepartamento}
            onChange={(e) => setFiltroDepartamento(e.target.value)}
          >
            <option value="all">Todos los departamentos</option>
            {obtenerDepartamentos().map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Estado:</label>
          <select
            id="status-filter"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value as 'all' | 'available' | 'on-leave')}
          >
            <option value="all">Todos</option>
            <option value="available">Disponibles</option>
            <option value="on-leave">Con licencia</option>
          </select>
        </div>

        <div className="results-summary">
          <span>{empleadosFiltrados.length} empleado(s)</span>
          {totalEnLicencia > 0 && (
            <span className="on-leave-count">{totalEnLicencia} con licencia</span>
          )}
        </div>
      </div>

      <div className="employee-table-container">
        <table className="employee-table">
          <thead>
            <tr>
              <th
                className={`sortable ${ordenarPor === 'name' ? `sorted-${ordenDireccion}` : ''}`}
                onClick={() => manejarOrden('name')}
              >
                Empleado
                <i className={`fas fa-sort${ordenarPor === 'name' ? `-${ordenDireccion === 'asc' ? 'up' : 'down'}` : ''}`}></i>
              </th>
              <th
                className={`sortable ${ordenarPor === 'department' ? `sorted-${ordenDireccion}` : ''}`}
                onClick={() => manejarOrden('department')}
              >
                Departamento
                <i className={`fas fa-sort${ordenarPor === 'department' ? `-${ordenDireccion === 'asc' ? 'up' : 'down'}` : ''}`}></i>
              </th>
              <th>Estado Actual</th>
              <th
                className={`sortable ${ordenarPor === 'used' ? `sorted-${ordenDireccion}` : ''}`}
                onClick={() => manejarOrden('used')}
              >
                Dias Usados
                <i className={`fas fa-sort${ordenarPor === 'used' ? `-${ordenDireccion === 'asc' ? 'up' : 'down'}` : ''}`}></i>
              </th>
              <th
                className={`sortable ${ordenarPor === 'remaining' ? `sorted-${ordenDireccion}` : ''}`}
                onClick={() => manejarOrden('remaining')}
              >
                Dias Restantes
                <i className={`fas fa-sort${ordenarPor === 'remaining' ? `-${ordenDireccion === 'asc' ? 'up' : 'down'}` : ''}`}></i>
              </th>
              <th>Proxima Licencia</th>
              {esAdmin && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {empleadosFiltrados.map(empleado => {
              const balance = balances.find(b => b.idEmpleado === empleado.id);
              const licenciaActual = obtenerLicenciaActual(empleado.id);
              const proximaLicencia = obtenerProximaLicencia(empleado.id);
              const estaEnLicencia = !!licenciaActual;

              return (
                <tr key={empleado.id} className={estaEnLicencia ? 'on-leave' : ''}>
                  <td data-label="Empleado">
                    <div className="employee-info">
                      <div className="employee-name">{empleado.nombre}</div>
                      <div className="employee-position">{empleado.cargo}</div>
                    </div>
                  </td>
                  <td data-label="Departamento">{empleado.departamento}</td>
                  <td data-label="Estado Actual">
                    {estaEnLicencia ? (
                      <div className={`status-badge on-leave ${obtenerColorTipo(licenciaActual.tipo)}`}>
                        <i className={obtenerIconoTipo(licenciaActual.tipo)}></i>
                        <div className="status-info">
                          <span>{licenciaActual.tipo}</span>
                          <small>{formatearRangoFechas(licenciaActual.fechaInicio, licenciaActual.fechaFin)}</small>
                        </div>
                      </div>
                    ) : (
                      <div className="status-badge available">
                        <i className="fas fa-check-circle"></i>
                        <span>Disponible</span>
                      </div>
                    )}
                  </td>
                  <td data-label="Dias Usados">
                    <span className="days-count used">{balance?.diasUsados || 0}</span>
                  </td>
                  <td data-label="Dias Restantes">
                    <span className="days-count remaining">
                      {balance?.diasRestantes || 0}
                      <span className="days-total-inline"> / {balance?.diasTotales || 0}</span>
                    </span>
                  </td>
                  <td data-label="Proxima Licencia">
                    {proximaLicencia ? (
                      <div className={`next-leave ${obtenerColorTipo(proximaLicencia.tipo)}`}>
                        <i className={obtenerIconoTipo(proximaLicencia.tipo)}></i>
                        <div className="next-leave-info">
                          <span>{formatearRangoFechas(proximaLicencia.fechaInicio, proximaLicencia.fechaFin)}</span>
                          <small>{proximaLicencia.tipo}</small>
                        </div>
                      </div>
                    ) : (
                      <span className="no-leave">-</span>
                    )}
                  </td>
                  {esAdmin && (
                    <td data-label="Acciones">
                      <div className="acciones-celda">
                        <button
                          className="btn-accion btn-editar"
                          onClick={() => abrirEditarEmpleado(empleado)}
                          title="Editar empleado"
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>
                        <button
                          className="btn-accion btn-asignar"
                          onClick={() => abrirAsignarVacaciones(empleado)}
                          title="Asignar vacaciones"
                        >
                          <i className="fas fa-calendar-plus"></i>
                        </button>
                        <button
                          className="btn-accion btn-config-dias"
                          onClick={() => abrirConfigurarBalance(empleado)}
                          title="Configurar dias de vacaciones"
                        >
                          <i className="fas fa-cog"></i>
                        </button>
                        <button
                          className="btn-accion btn-eliminar-accion"
                          onClick={() => abrirConfirmarEliminar(empleado)}
                          title="Eliminar empleado"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {empleadosFiltrados.length === 0 && (
        <div className="no-results">
          <i className="fas fa-users"></i>
          <h3>No se encontraron empleados</h3>
          <p>No hay empleados que coincidan con los filtros seleccionados.</p>
        </div>
      )}

      {/* Modales */}
      {mostrarFormEmpleado && (
        <FormularioEmpleado
          empleado={empleadoSeleccionado}
          departamentos={departamentos}
          onGuardar={manejarGuardarEmpleado}
          onCancelar={() => { setMostrarFormEmpleado(false); setEmpleadoSeleccionado(null); }}
        />
      )}

      {mostrarFormPeriodo && (
        <FormularioPeriodo
          periodo={null}
          empleados={empleados}
          tiposLicencia={tiposLicencia}
          balances={balances}
          periodos={periodos}
          empleadoPreseleccionado={empleadoSeleccionado?.id}
          onGuardar={manejarGuardarPeriodo}
          onCancelar={() => { setMostrarFormPeriodo(false); setEmpleadoSeleccionado(null); }}
        />
      )}

      {mostrarFormBalance && empleadoSeleccionado && (
        <FormularioBalance
          empleado={empleadoSeleccionado}
          balance={balances.find(b => b.idEmpleado === empleadoSeleccionado.id)}
          anio={ANIO_BALANCE}
          onGuardar={manejarGuardarBalance}
          onCancelar={() => { setMostrarFormBalance(false); setEmpleadoSeleccionado(null); }}
        />
      )}

      <ModalConfirmacion
        visible={mostrarConfirmEliminar}
        titulo="Eliminar Empleado"
        mensaje={`Esta seguro de que desea eliminar a "${empleadoSeleccionado?.nombre}"? Esta accion no se puede deshacer.`}
        onConfirmar={manejarEliminarEmpleado}
        onCancelar={() => { setMostrarConfirmEliminar(false); setEmpleadoSeleccionado(null); }}
        cargando={eliminando}
      />
    </div>
  );
};

export default ListaEmpleados;
